import Vue, { CreateElement } from '@src/vue'
import { Component, Watch } from 'vue-property-decorator'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { Link } from '@src/elements/link/link'
import { Spacer } from '@src/elements/spacer/spacer'
import { MemberActivity } from '@src/data/member-activity'
import { MemberActivityFilters } from '@src/state/store'
import { DEFAULT_PAGE_SIZE, MIN_LANDSCAPE_WIDTH, Unit } from '@src/util/constants'
import { ErrorScreen } from '@src/components/error-screen/error-screen'
import { H6 } from '@src/elements/typography/typography'
import { TextCell } from '@src/components/table-list/cells'
import { ActivityNameCell } from '@src/routes/members/components/activities/activity-name-cell'
import { StatusCell } from '@src/routes/members/components/activities/status-cell'
import { isMobileBrowser, track } from '@src/util/functions'
import { ActivityService } from '@src/service/activity'
import { convertToLocalDate } from '@src/util/date-format'
import { CustomPaginationTableList } from '@src/components/table-list/custom-pagination-table-list'
import { Select } from '@src/elements/select/select'
import { OptionGroup } from '@src/elements/select/option-group'
import { Option } from '@src/elements/select/option'
import { Legend } from '@src/components/legend/legend'

import './activities.sass'
import { Button } from '@src/elements/button/button'
import SessionManager from '@src/lib/session_manager'
import Member from '@src/data/member'
import Club from '@src/data/club'
import moment from 'moment'
import { MemberActivitiesTooltip } from '@src/routes/members/member-activities-tooltip'
import { TranslateResult } from 'vue-i18n'

export enum MemberActivityType {
  ACTIVITIES = 'activity' ,
  PIX_WORKOUT = 'pixWorkout',
  CLASS = 'pixInteractiveClass',
  PIX_HOME_WORKOUT = 'pixHomeWorkout'
}

export enum MemberActivityStatusType {
  COMPLETED = 'completed' ,
  PLANNED = 'planned'
}

@Component({
  computed: {
    activityFilters () {
      return this.$store.state.member_activities.memberActivityFilters
    },
    member () {
      return this.$store.state.selectedMember
    },
    club () {
      return this.$store.state.club
    }
  }
})
export class MemberActivities extends Vue {

  member: Member
  club: Club
  windowWidth: number = window.innerWidth

  showMemberActivitiesTooltip: boolean = false
  protected currentPage = 1
  protected totalPages = 1
  protected loading: boolean = true
  protected activities: MemberActivity[] = []
  protected activityFilters: MemberActivityFilters

  constructor () {
    super()
    this.resetFilters()
    track('/members/member_activities')
  }

  @Watch('activityFilters')
  filtersChanged () {
    return this.changePage(1)
  }

  filterByType (value) {
    this.$store.dispatch('filterActivities', { type: value })
  }

  filterActivitiesByStatusOnDevices (value) {
    if (value !== '') {
      this.activityFilters.status = new Set<string>([value])
    } else {
      this.activityFilters.status = new Set<string>([MemberActivityStatusType.PLANNED, MemberActivityStatusType.COMPLETED])
    }
    this.$store.dispatch('filterActivities', { status: this.activityFilters.status })
  }

  filterActivitiesByStatus (value) {
    this.activityFilters.status.has(value) ? this.activityFilters.status.delete(value) : this.activityFilters.status.add(value)
    this.$store.dispatch('filterActivities', { status: this.activityFilters.status })
  }

  resetFilters () {
    this.$store.dispatch('filterActivities', { status: new Set<string>(['completed', 'planned']), type: '' })
  }

  changePage (page) {
    this.currentPage = page
    return this.loadActivities()
  }

  print () {
    window.print()
  }

  openActivitiesPopUp () {
    this.showMemberActivitiesTooltip = true
  }

  closeActivitiesPopUp (loadActivities = false) {
    this.showMemberActivitiesTooltip = false
    if (loadActivities) {
      this.loadActivities()
    }
  }

  completeActivity (activity) {
    const data = {
      member_activity: {
        status: MemberActivityStatusType.COMPLETED,
        activity_id: activity.activityTypeId,
        amount: activity.amount,
        started_at: activity.date
      }
    }

    return new ActivityService().completeActivity(activity.id, data).then(() => this.loadActivities())
  }

  deleteActivity (activity) {
    return new ActivityService().deleteActivity(activity.id).then(() => this.loadActivities())
  }

  render (h: CreateElement) {
    if (this.loading) {
      return <Loader />
    }

    const activities = this.activities.map(activity => {
      const key = activity.activity.key
      let dynamicName
      if (key === MemberActivityType.CLASS) {
        dynamicName = activity.meta.clubClassName || activity.meta.workoutName || ''
      } else if (key === MemberActivityType.PIX_HOME_WORKOUT) {
        dynamicName = activity.meta.workoutName || this.$t('PAGE.MEMBERS.ACTIVITIES.TYPES.PIX_HOME_WORKOUT')
      } else if (key === MemberActivityType.PIX_WORKOUT) {
        dynamicName = this.$t('PAGE.MEMBERS.ACTIVITIES.TYPES.STATION_WORKOUT')
      } else {
        dynamicName = this.$t(`ACTIVITIES.${key.toUpperCase()}`)
      }

      return {
        id: activity.id,
        activity: key,
        name: dynamicName,
        duration: this.formatActivityAmount(activity),
        date: convertToLocalDate(activity.startedAt),
        mets: `${activity.metValue} ${this.$t('PAGE.MEMBERS.ACTIVITIES.MET')}`,
        activityStatus: activity.status,
        showOptions: !SessionManager.Instance.isManager(),
        amount: activity.amount,
        activityTypeId: activity.activity.id
      }
    })

    let headers: { [key: string]: { headerLabel: TranslateResult, type: string, disableSorting: boolean } } = {
      activity: { headerLabel: this.$t('PAGE.MEMBERS.ACTIVITIES.ACTIVITY'), type: 'string', disableSorting: true },
      duration: { headerLabel: this.$t('PAGE.MEMBERS.ACTIVITIES.DURATION'), type: 'string', disableSorting: true },
      date: { headerLabel: this.$t('DATE'), type: 'date', disableSorting: true },
      mets: { headerLabel: this.$t('PAGE.MEMBERS.ACTIVITIES.MET'), type: 'float', disableSorting: true },
      activityStatus: { headerLabel: this.$t('PAGE.MEMBERS.ACTIVITIES.STATUS.STATUS'), type: 'string', disableSorting: true }
    }
    let options: { label: TranslateResult, onClickCallback: (activity: any) => Promise<void> }[] = []

    if (!SessionManager.Instance.isManager()) {
      headers = {
        ...headers,
        actions: { headerLabel: this.$t('ACTIONS'), type: 'string', disableSorting: true }
      }
      options = [
        {
          label: this.$t('COMPLETE'),
          onClickCallback: this.completeActivity
        },
        {
          label: this.$t('DELETE'),
          onClickCallback: this.deleteActivity
        }
      ]

    }

    return <div class='activities-wrapper scroll-content'>
      {SessionManager.Instance.isManager() && (this.windowWidth < MIN_LANDSCAPE_WIDTH || isMobileBrowser()) ? (
        <div class='print-header'>
          <div class='print-date'>{moment(new Date()).format('MM/DD/YYYY, h:mm A')}</div>
          <div class='print-page-title'>{document.title}</div>
        </div>
      ) : ''}
      {SessionManager.Instance.isManager() ? <div class='print-details'><H6>{this.club!.name} | {this.member.fullName}</H6><Spacer /></div> : ''}
      <div class='row'>
        <div class='row inner-row'>
          {this.renderFilterByType(h)}
          {this.renderFilterByStatus(h)}
          <div id='activities-right-container-wrapper'>
            {SessionManager.Instance.isManager() ? <Button type='remove' click={this.print} icon={'util/print'} label={this.$t('PRINT')}
                                                           class='print-btn' /> : ''}
            <Legend select={this.filterActivitiesByStatus}
                    items={[
                      { type: 'completed', label: this.$t('PAGE.MEMBERS.ACTIVITIES.STATUS.COMPLETED'), color: 'blue', disabled: !this.activityFilters.status.has('completed') },
                      { type: 'planned', label: this.$t('PAGE.MEMBERS.ACTIVITIES.STATUS.PLANNED'), color: 'red', disabled: !this.activityFilters.status.has('planned') }
                    ]} />
          </div>
        </div>

        {!SessionManager.Instance.isManager() ?
          <div class='activities-toggle-wrapper'>
            <Button type='remove' click={this.openActivitiesPopUp} icon={'util/add'}/>
            {this.showMemberActivitiesTooltip ? <MemberActivitiesTooltip closeCallback={this.closeActivitiesPopUp} /> : ''}
          </div> : ''}
        </div>

        {activities.length === 0 ? this.renderEmptyScreen(h)

          : <CustomPaginationTableList class={'activities-list'} items={activities}
                       cells={{
                         activity: ActivityNameCell,
                         duration: TextCell,
                         date: TextCell,
                         mets: TextCell,
                         activityStatus: StatusCell
                       }}
                       header={headers}
                       noOfPages={this.totalPages}
                       changePageCallback={this.changePage} currentPage={this.currentPage}
                       options={options}
          />
        }
    </div>
  }

  renderFilterByType (h: CreateElement) {
    let typeFilterLabel = this.$t(`PAGE.MEMBERS.ACTIVITIES.TYPES.${{
      '': 'ALL',
      'activity': 'ACTIVITIES',
      'pixWorkout': 'STATION_WORKOUTS',
      'pixInteractiveClass': 'CLASSES',
      'pixHomeWorkout': 'PIX_HOME_WORKOUT'
    }[`${this.activityFilters.type}`]}`)
    return <Select filter={true} ref='typeFilter' cssClass='activity-types' value={typeFilterLabel} label={this.$t('PAGE.MEMBERS.ACTIVITIES.TYPE_OF_ACTIVITY')}>
      <OptionGroup>
        <Option type='single-option' value={''} select={this.filterByType} label={this.$t('PAGE.MEMBERS.ACTIVITIES.TYPES.ALL')}/>
        <Option type='single-option' value={MemberActivityType.ACTIVITIES} select={this.filterByType}
                label={this.$t('PAGE.MEMBERS.ACTIVITIES.TYPES.ACTIVITIES')}/>
        <Option type='single-option' value={MemberActivityType.PIX_WORKOUT} select={this.filterByType}
                label={this.$t('PAGE.MEMBERS.ACTIVITIES.TYPES.STATION_WORKOUTS')}/>
        <Option type='single-option' value={MemberActivityType.PIX_HOME_WORKOUT} select={this.filterByType}
                label={this.$t('PAGE.MEMBERS.ACTIVITIES.TYPES.PIX_HOME_WORKOUTS')} />
        <Option type='single-option' value={MemberActivityType.CLASS} select={this.filterByType}
                label={this.$t('PAGE.MEMBERS.ACTIVITIES.TYPES.CLASSES')}/>
      </OptionGroup>
    </Select>
  }

  renderFilterByStatus (h: CreateElement) {
    let typeFilterLabel = this.$t('PAGE.MEMBERS.ACTIVITIES.TYPES.ALL')
    if (this.activityFilters.status.size === 1 && this.activityFilters.status.has('completed')) {
      typeFilterLabel = this.$t('PAGE.MEMBERS.ACTIVITIES.STATUS.COMPLETED')
    } else if (this.activityFilters.status.size === 1 && this.activityFilters.status.has('planned')) {
      typeFilterLabel = this.$t('PAGE.MEMBERS.ACTIVITIES.STATUS.PLANNED')
    }
    return <div class={'status-types'}>
        <Select filter={true} ref='statusFilter' cssClass='status-types' value={typeFilterLabel} label={this.$t('PAGE.MEMBERS.ACTIVITIES.STATUS.STATUS')}>
        <OptionGroup>
          <Option type='single-option' value={''} select={this.filterActivitiesByStatusOnDevices} label={this.$t('PAGE.MEMBERS.ACTIVITIES.TYPES.ALL')}/>
          <Option type='single-option' value={MemberActivityStatusType.COMPLETED} select={this.filterActivitiesByStatusOnDevices}
                  label={this.$t('PAGE.MEMBERS.ACTIVITIES.STATUS.COMPLETED')}/>
          <Option type='single-option' value={MemberActivityStatusType.PLANNED} select={this.filterActivitiesByStatusOnDevices}
                  label={this.$t('PAGE.MEMBERS.ACTIVITIES.STATUS.PLANNED')}/>
        </OptionGroup>
      </Select>
    </div>
  }

  hasFilters () {
    return !!this.activityFilters.type || this.activityFilters.status.size !== 2
  }

  renderEmptyScreen (h: CreateElement) {
    if (!this.hasFilters()) {
      return <ErrorScreen message={this.$t('PAGE.MEMBERS.ACTIVITIES.NO_ACTIVITIES_FIND')} class='activities-empty' />
    }
    return <ErrorScreen message={this.$t('PAGE.MEMBERS.ACTIVITIES.FILTER_ERROR')}>
      <Link variant='colored' ref='resetFilters' align='center' click={this.resetFilters} label={this.$t('PAGE.MEMBERS.ACTIVITIES.RESET_FILTERS')} />
    </ErrorScreen>
  }

  formatActivityAmount (memberActivity) {
    if (memberActivity.activity.unit === Unit.SEC) {
      return `${this.formatDuration(memberActivity.amount)}`
    } else if (memberActivity.activity.unit === Unit.STEPS) {
      return `${memberActivity.amount} ${this.$t('STEPS')}`
    } else if (memberActivity.activity.unit === Unit.FLOORS) {
      return `${memberActivity.amount} ${this.$t('FLOORS')}`
    }
    return memberActivity.amount
  }

  formatDuration (seconds) {
    if (seconds < 60) {
      return `${seconds} ${this.$t('UNITS.S')}`
    } else if (seconds >= 60 && seconds < 3600) {
      const min = Math.floor((seconds / 60) % 60)
      const sec = Math.floor(seconds % 60)
      const secondsLabel = sec ? ` ${sec}${this.$t('UNITS.S')}` : ''

      return `${min}${this.$t('UNITS.MIN')}${secondsLabel}`
    } else if (seconds >= 3600) {
      const hour = Math.floor((seconds / (60 * 60)) % 24)
      const min = Math.floor((seconds / 60) % 60)
      const sec = Math.floor(seconds % 60)
      const minutesLabel = min || sec ? ` ${min}${this.$t('UNITS.MIN')}` : ''
      const secondsLabel = sec ? ` ${sec}${this.$t('UNITS.S')}` : ''
      return `${hour}${this.$t('UNITS.H')}${minutesLabel}${secondsLabel}`
    }
  }

  loadActivities () {
    if (!this.activityFilters.status.size) {
      this.activities = []
      this.totalPages = 0
      return undefined
    }

    this.loading = true
    const statusFilter = this.activityFilters.status.size === 1 ? this.activityFilters.status.has('planned') ? 'planned' : 'completed' : undefined
    return new ActivityService().getPage(this.$route.params.id, this.currentPage, DEFAULT_PAGE_SIZE, this.activityFilters.type, statusFilter)
      .then(activities => {
        this.activities = activities.memberActivities
        this.totalPages = activities.totalPages
      })
      .finally(() => this.loading = false)
  }

  created () {
    window.addEventListener('orientationchange', this.updateSize)
    window.addEventListener('resize', this.updateSize)
    this.windowWidth = window.innerWidth
    return this.loadActivities()
  }

  destroyed () {
    window.removeEventListener('orientationchange', this.updateSize)
    window.removeEventListener('resize', this.updateSize)
  }

  updated () {
    this.setDocumentTitle()
  }

  updateSize () {
    this.windowWidth = window.innerWidth
  }

  setDocumentTitle () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('MEMBERS')} - ${this.$root.$t('PAGE.MEMBERS.DETAIL.TABS.ACTIVITIES')}`
  }
}
